import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/constants/site";
import { scrollToId } from "@/lib/scroll";

const TERMS = [
  {
    n: "01",
    title: "Volume & Pricing Estimates",
    text: "All estimates provided online, via phone, or through text are non-binding approximations. Our team will provide a final, binding price on-site, determined by the actual volume your items occupy in our vehicle prior to loading.",
  },
  {
    n: "02",
    title: "Access & Labor Fees",
    text: "Our base pricing assumes clear pathways, ground-level access, or standard curbside placement. Additional handling fees will apply for long-distance hauls (exceeding 100 feet to the truck) or when navigating multiple flights of stairs.",
  },
  {
    n: "03",
    title: "Hazardous Materials",
    text: "For safety and environmental compliance, we strictly do not accept hazardous waste. This includes wet paint, motor chemicals, car batteries, fuel, pressurized tanks, or bio-hazardous materials. The client is responsible for disclosing any hidden hazards.",
  },
  {
    n: "04",
    title: "Damage & Liability",
    text: "While our team exercises maximum care to navigate your property safely, Take The Leap Junk Removal LLC is not liable for minor scratches, scuffs, or cosmetic wear to walls, floors, or doors during the removal of oversized or awkwardly shaped items.",
  },
  {
    n: "05",
    title: "Ownership of Items",
    text: "By authorizing our team to load your items, you confirm that you are the legal owner of the property or possess explicit authorization from the owner to dispose of it. All items are deemed abandoned upon loading and cannot be returned.",
  },
  {
    n: "06",
    title: "Payment Terms",
    text: "Payment is required immediately upon completion of the service. We accept major credit cards and digital payments, securely processed on-site via Jobber. Delayed payments will incur a $35 late fee after 48 hours.",
  },
  {
    n: "07",
    title: "Cancellation & Rescheduling",
    text: "We understand that plans may change. To avoid a $50.00 short-notice fee, please notify us of any cancellations or rescheduling requests at least 24 hours prior to your scheduled arrival window. Jobs canceled at the door or while our truck is en-route will incur a $75.00 trip fee to cover fuel and labor costs.",
  },
  {
    n: "08",
    title: "Mattress Bed Bug Policy",
    text: "For the safety of our crew and to prevent cross-contamination, any mattress or box spring with an active or suspected bed bug infestation must be completely wrapped and sealed in a plastic mattress disposal bag by the client prior to our arrival. Infested items left unsealed will not be loaded or may be subject to a $50.00 hazardous handling surcharge at the discretion of the crew.",
  },
];

const PRIVACY = [
  {
    title: "Information We Collect",
    text: "We only collect personal information that you voluntarily provide to us when booking a job, requesting an estimate, or contacting us. This includes:",
    bullets: [
      "Contact Information: your name, phone number, email address, and physical service address.",
      "Job Details: descriptions or photos of the items you need removed.",
      "Payment Information: credit card or banking details used to settle invoices (processed securely through our third-party billing provider, Jobber; we do not store your raw payment details on our systems).",
    ],
  },
  {
    title: "How We Use Your Information",
    text: "We use the information we collect strictly to operate our junk removal business, including to:",
    bullets: [
      "Schedule, confirm, and execute your junk removal appointments.",
      "Send you accurate quotes, updates regarding our arrival windows, and final invoices.",
      "Respond directly to your customer service questions or requests.",
      "Improve our website and local service offerings around Hanover and surrounding areas.",
    ],
  },
  {
    title: "Data Sharing and Protection",
    bullets: [
      "We Do Not Sell Your Data: we will never sell, rent, or trade your personal information to outside marketing companies.",
      "Trusted Partners: we only share your data with trusted service providers necessary to run our business, such as Jobber (for job booking and secure payment processing). These platforms use industry-standard encryption to keep your data safe.",
      "Legal Compliance: we may disclose your information if required to do so by law or to protect the safety and rights of our crew.",
    ],
  },
  {
    title: "Your Choices",
    text: "You can opt out of receiving promotional texts or emails from us at any time by replying “STOP” to any text or clicking “Unsubscribe” in our emails. You can also contact us directly to update or request the deletion of your customer contact records.",
  },
  {
    title: "Contact Us",
    text: `If you have any questions about this Privacy Policy or how we handle your data, contact Take The Leap Junk Removal LLC at ${SITE.email} or ${SITE.phoneDisplay}.`,
  },
];

export default function Legal() {
  const location = useLocation();
  useEffect(() => {
    document.title = "Terms & Privacy — Take The Leap Junk Removal";
    const id = location.hash.replace("#", "");
    if (["terms", "privacy"].includes(id)) {
      const t = setTimeout(() => scrollToId(id), 300);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0 });
  }, [location.hash]);

  return (
    <div data-testid="legal-page" className="bg-ink font-sans text-zinc-100 antialiased">
      <div className="noise-overlay" aria-hidden="true" />
      <Nav />
      <main className="pt-24">
        <section className="mx-auto max-w-7xl px-6 pb-8 pt-16 md:px-10 md:pt-24">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-acid">Terms & privacy</p>
            <h1 className="max-w-4xl font-display text-5xl font-black italic uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl">
              The fine print. <span className="text-acid">In plain English.</span>
            </h1>
          </Reveal>
        </section>

        <section id="terms" data-testid="terms-section" className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <Reveal>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Chapter 01</p>
            <h2 className="mb-4 font-display text-3xl font-black italic uppercase tracking-tight sm:text-4xl">
              Terms & <span className="text-acid">Service Disclaimers</span>
            </h2>
            <p className="mb-10 text-sm text-zinc-500">Take The Leap Junk Removal LLC</p>
          </Reveal>
          <div className="border-t border-seam">
            {TERMS.map((t, i) => (
              <Reveal key={t.n} delay={i * 0.03}>
                <div data-testid={`terms-item-${t.n}`} className="grid gap-3 border-b border-seam py-8 md:grid-cols-12 md:gap-8">
                  <span className="text-outline-acid font-display text-4xl font-black italic leading-none md:col-span-2">
                    {t.n}
                  </span>
                  <h3 className="font-display text-xl font-extrabold italic uppercase tracking-tight md:col-span-4">
                    {t.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-zinc-400 md:col-span-6">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section id="privacy" data-testid="privacy-section" className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <Reveal>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Chapter 02</p>
            <h2 className="mb-4 font-display text-3xl font-black italic uppercase tracking-tight sm:text-4xl">
              Privacy <span className="text-acid">Policy</span>
            </h2>
            <p className="mb-4 text-sm text-zinc-500">Effective date: August 26, 2026</p>
            <p className="mb-10 max-w-3xl text-sm leading-relaxed text-zinc-400">
              At Take The Leap Junk Removal LLC, we value your privacy and are committed to protecting your
              personal information. This Privacy Policy explains how we collect, use, and secure your data
              when you visit our website, request a quote, or book our services.
            </p>
          </Reveal>
          <div className="border-t border-seam">
            {PRIVACY.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.03}>
                <div data-testid={`privacy-item-${i + 1}`} className="grid gap-3 border-b border-seam py-8 md:grid-cols-12 md:gap-8">
                  <span className="text-outline-acid font-display text-4xl font-black italic leading-none md:col-span-2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl font-extrabold italic uppercase tracking-tight md:col-span-4">
                    {s.title}
                  </h3>
                  <div className="md:col-span-6">
                    {s.text && <p className="text-sm leading-relaxed text-zinc-400">{s.text}</p>}
                    {s.bullets && (
                      <ul className={`flex flex-col gap-2 ${s.text ? "mt-3" : ""}`}>
                        {s.bullets.map((b) => (
                          <li key={b.slice(0, 24)} className="flex items-start gap-3 text-sm leading-relaxed text-zinc-400">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 -skew-x-12 bg-acid" aria-hidden="true" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
